import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface DayCapacity {
  day: string;
  shortDay: string;
  date: number;
  hours: number;
  capacity: number;
}

interface ProgressWidgetProps {
  dailyData?: DayCapacity[];
  weekLabel?: string;
  totalHours?: number;
  targetHours?: number;
}

// Get week dates for a given week offset from current
function getWeekDates(weekOffset: number = 0): { weekLabel: string; days: DayCapacity[] } {
  const now = new Date();
  const currentDay = now.getDay();
  const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
  
  const monday = new Date(now);
  monday.setDate(now.getDate() + mondayOffset + (weekOffset * 7));
  
  const weekNum = Math.ceil((monday.getDate() + new Date(monday.getFullYear(), monday.getMonth(), 1).getDay()) / 7);
  const month = monday.toLocaleDateString('en-US', { month: '2-digit' });
  const weekLabel = `w${month}.${weekNum.toString().padStart(2, '0')}`;
  
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const shortDays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  
  const days: DayCapacity[] = dayNames.map((day, i) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    return {
      day,
      shortDay: shortDays[i],
      date: date.getDate(),
      hours: 0, // Would be populated from actual data
      capacity: 8, // Default capacity
    };
  });
  
  return { weekLabel, days };
}

export function ProgressWidget({ 
  dailyData,
  weekLabel: propWeekLabel,
  totalHours = 0,
  targetHours = 40
}: ProgressWidgetProps) {
  const [weekOffset, setWeekOffset] = useState(0);
  
  const { weekLabel, days } = useMemo(() => {
    if (dailyData && propWeekLabel) {
      return { weekLabel: propWeekLabel, days: dailyData };
    }
    return getWeekDates(weekOffset);
  }, [weekOffset, dailyData, propWeekLabel]);
  
  const goToPrevWeek = () => setWeekOffset(prev => prev - 1);
  const goToNextWeek = () => setWeekOffset(prev => prev + 1);
  const goToCurrentWeek = () => setWeekOffset(0);
  
  // Calculate total hours for current view
  const currentTotalHours = dailyData 
    ? dailyData.reduce((sum, day) => sum + day.hours, 0)
    : totalHours;
  
  const progressPercentage = Math.min((currentTotalHours / targetHours) * 100, 100);
  
  return (
    <div className="bg-white rounded-md shadow-[0px_2px_15px_-3px_rgba(0,0,0,0.07),0px_10px_20px_-2px_rgba(0,0,0,0.04)] p-6 h-[220px] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-[12px] text-[#808080] uppercase tracking-wide">Weekly Progress</p>
        <div className="flex items-center gap-2">
          <button
            onClick={goToPrevWeek}
            className="w-6 h-6 flex items-center justify-center rounded-md border border-[#e6e6e6] hover:bg-[#f6f6f6] transition-colors"
          >
            <ChevronLeft className="w-3 h-3 text-[#808080]" />
          </button>
          <button
            onClick={goToCurrentWeek}
            className="text-[11px] text-[#808080] px-2 py-1 rounded-md border border-[#e6e6e6] hover:bg-[#f6f6f6] transition-colors"
          >
            {weekLabel}
          </button>
          <button
            onClick={goToNextWeek}
            className="w-6 h-6 flex items-center justify-center rounded-md border border-[#e6e6e6] hover:bg-[#f6f6f6] transition-colors"
          >
            <ChevronRight className="w-3 h-3 text-[#808080]" />
          </button>
        </div>
      </div>
      
      {/* Overall Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] text-[#808080]">Total</span>
          <span className="text-[11px] font-medium text-[#262626]">
            {currentTotalHours.toFixed(1)}h / {targetHours}h
          </span>
        </div>
        <div className="h-2 bg-[#f5f5f5] rounded-full overflow-hidden">
          <div 
            className="h-full bg-[#e30840] transition-all duration-300 rounded-full"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>
      
      {/* Daily Bars */}
      <div className="flex-1 flex items-end justify-between gap-2">
        {days.map((day, index) => {
          const dayPercentage = Math.min((day.hours / day.capacity) * 100, 100);
          const isToday = weekOffset === 0 && index === (new Date().getDay() + 6) % 7;
          
          return (
            <div key={day.day} className="flex flex-col items-center flex-1 gap-1">
              {/* Bar */}
              <div className="w-full h-[80px] flex flex-col justify-end">
                <div 
                  className={`w-full rounded-t-sm transition-all duration-300 ${
                    isToday ? 'bg-[#e30840]' : 'bg-[#4A90E2]'
                  }`}
                  style={{ height: `${dayPercentage}%`, minHeight: day.hours > 0 ? '4px' : '0' }}
                />
              </div>
              
              {/* Day Label */}
              <span className={`text-[10px] ${isToday ? 'font-bold text-[#e30840]' : 'text-[#808080]'}`}>
                {day.shortDay}
              </span>
              
              {/* Hours */}
              <span className="text-[9px] text-[#b3b3b3]">
                {day.hours > 0 ? `${day.hours}h` : '-'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
